/**
 * What a display name may contain.
 *
 * A handle is machine-readable and has always been fenced in (see
 * usernames.js). A display name is the opposite — it is whatever somebody
 * calls themselves, in whatever script — so the rules here are as few as
 * possible and stop at one thing: no emoji.
 *
 * Why that one. A name is not only a label on a profile; it is drawn inside
 * running text ("Алиса удалила сообщение"), in a system notification, in a
 * call banner, and on the Android lock screen. Pictographs there are not
 * decoration — they are how a name is made to imitate a badge, a read receipt
 * or a verified mark, and they make two accounts indistinguishable at a
 * glance in a list of chats. They also break line height in every row they
 * appear in.
 *
 * The check is deliberately about pictographs, not about "non-Latin": Cyrillic,
 * Arabic, Hebrew, Chinese, Japanese, Korean, Greek and every other script a
 * person actually has a name in are all perfectly fine.
 */

/*
 * Extended_Pictographic is the property that covers emoji proper — including
 * the ones with no colour presentation, like ☎ — without dragging in ordinary
 * symbols such as + or ©. Alongside it:
 *
 *   Regional_Indicator   the letter pairs that make flags
 *   FE0F / FE0E          the variation selectors that force emoji or text style
 *   200D                 the zero-width joiner that glues 👩 + 🚀 together
 *   1F3FB–1F3FF          skin-tone modifiers
 *   20E3                 the enclosing keycap
 *
 * The last five are matched separately so that removing a pictograph does not
 * leave its invisible scaffolding behind in the name.
 */
const EMOJI = /[\p{Extended_Pictographic}\p{Regional_Indicator}\u{1F3FB}-\u{1F3FF}\u{20E3}️︎‍]/gu

export const NAME_MAX = 64

export function hasEmoji (input) {
  EMOJI.lastIndex = 0
  return EMOJI.test(String(input || ''))
}

/**
 * A name with the pictographs taken out.
 *
 * Used as a last line of defence rather than as the main check: the screens
 * say no before anything is saved, and this is what keeps a name arriving
 * from somewhere else — an older build, a linked device, a restored profile —
 * from putting one back.
 */
export function cleanName (input) {
  return String(input || '')
    .replace(EMOJI, '')
    // Collapsing whitespace matters after a removal: "Алиса 🌲 Б" must not
    // come out as "Алиса  Б" with a gap where the tree was.
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, NAME_MAX)
}

/**
 * @returns null when the name is fine, or a reason code the UI turns into a
 *   sentence: 'empty', 'emoji', 'long'.
 */
export function nameError (input) {
  const name = String(input || '').trim()
  if (!name) return 'empty'
  if (hasEmoji(name)) return 'emoji'
  if (name.length > NAME_MAX) return 'long'
  return null
}
